// Lib
import { Capacitor } from '@capacitor/core';

// Utils
import { getStoredGridSize } from '../../utils/grid/gridUtils';
import { getLastAppMode } from './platformAppModeUtils';
import {
    getUserAgent,
    isChromeUserAgent,
    isEdgeUserAgent,
    isFirefoxUserAgent,
    isTabletUserAgent,
} from './platformBrowserUtils';
import { getIsElectronMac, isInIFrame, isIosPwa, isNonIosDevicePwa } from './platformAppTypeUtils';
import {
    getIsRetinaPlatform,
    getIsTouchPlatform,
    getPlatformSupportsMouse,
    getPlatformSupportsWebSockets,
    platformSupportsContentEditable,
    platformSupportsIndexedDb,
    platformSupportsLocalStorage,
    platformSupportsServiceWorker,
    platformSupportsSvg,
    platformSupportsWebWorkers,
} from './platformFeatureDetectionUtils';

// Types
import {
    ApplicationPlatform,
    ApplicationType,
    BrowserEngineType,
    DeviceFormFactor,
    MilanoteApplicationMode,
    OperatingSystem,
    PlatformDetails,
    PlatformType,
} from '../../../common/platform/platformTypes';
import { GRID } from '../../utils/grid/gridConstants';
import getClientConfig from '../../utils/getClientConfig';
import globalLogger from '../../logger';

const logger = globalLogger.createChannel('platform-detection');

declare global {
    interface Window {
        safari?: unknown;
    }
}

/**
 * Gets the platform type based on the user agent and app version.
 */
// TODO-MOBILE - NOTE: This is mostly a reimplementation of the old platform detection code.
//  We might want to consider using a library to get a more accurate platform type.
const getPlatformType = ({
    userAgent,
    browserAppVersion,
    screenWidth,
    screenHeight,
    isTouch,
    devicePixelRatio,
}: {
    userAgent?: string;
    browserAppVersion?: string;
    screenWidth: number;
    screenHeight: number;
    isTouch: boolean;
    devicePixelRatio: number;
}): PlatformType => {
    if (!userAgent) return PlatformType.unknown;

    if (userAgent.match(/iPad/i)) return PlatformType.ipad;
    if (userAgent.match(/iPhone/i)) return PlatformType.iphone;
    if (userAgent.match(/iPod/i)) return PlatformType.ipod;
    if (userAgent.match(/Android/i)) return PlatformType.android;

    if (!browserAppVersion) return PlatformType.unknown;

    if (browserAppVersion.indexOf('Win') !== -1) return PlatformType.windows;
    if (browserAppVersion.indexOf('Mac') !== -1) {
        // From iPadOS 13 (released in 2019), Safari on iPad is a "desktop-class browser" (Apple's term)
        // The implication there is that the user agent sent from Safari on iPad is Mac instead of iPad.
        // So to differentiate iPad and Mac, check for touch (at the time of commit, there are no touch screen Macs)
        if (getIsTouchPlatform()) return PlatformType.ipad;
        return PlatformType.mac;
    }
    if (browserAppVersion.indexOf('X11') !== -1) {
        // Samsung tablets and other Android devices report as X11 - as they are based on Linux
        //  - The Chrome browser user agent has no other way to identify the platform
        // In this case, we're going to check the dimensions of the screen & touch capability
        //  to make a guess at whether it's a tablet or not.

        const minDimension = Math.min(screenWidth, screenHeight);
        const maxDimension = Math.max(screenWidth, screenHeight);
        const isTablet = isTouch && devicePixelRatio > 1 && minDimension < 800 && maxDimension < 1300;

        return isTablet ? PlatformType.android : PlatformType.unix;
    }
    if (browserAppVersion.indexOf('Linux') !== -1) return PlatformType.linux;

    return PlatformType.unknown;
};

/**
 * Gets the form factor of the device - phone, tablet, or desktop.
 */
const getFormFactor = (userAgent: string, platformType: PlatformType, screenWidth: number): DeviceFormFactor => {
    if (platformType === PlatformType.ipad) return DeviceFormFactor.tablet;

    if (platformType === PlatformType.iphone || platformType === PlatformType.ipod) return DeviceFormFactor.phone;

    if (isTabletUserAgent(userAgent)) return DeviceFormFactor.tablet;

    // On Android, we prevent all orientations except portrait (in native code) when largest length is less than 600,
    // so we use that here to "determine" the experience to present.
    if (platformType === PlatformType.android)
        return screenWidth < 600 ? DeviceFormFactor.phone : DeviceFormFactor.tablet;

    return DeviceFormFactor.desktop;
};

/**
 * Gets the platform as reported by Capacitor.
 */
const getCapacitorPlatform = (): string => Capacitor.getPlatform();

/**
 * Gets the Milanote application mode to initialise the app in.
 */
const getInitialAppMode = (formFactor: DeviceFormFactor): MilanoteApplicationMode => {
    if (Capacitor.isNativePlatform()) {
        if (formFactor === DeviceFormFactor.tablet) return MilanoteApplicationMode.desktop;
        return MilanoteApplicationMode.mobile;
    }

    const lastAppMode = getLastAppMode();
    if (lastAppMode) return lastAppMode;

    if (formFactor === DeviceFormFactor.phone) return MilanoteApplicationMode.mobile;

    // If we already in mobile mode, we should stick to it.
    if (getStoredGridSize()?.name === GRID.MOBILE.name) return MilanoteApplicationMode.mobile;

    return MilanoteApplicationMode.desktop;
};

/**
 * Determines the initial operating system based on the platform type.
 */
const getInitialOperatingSystem = (platformType: PlatformType): OperatingSystem => {
    switch (platformType) {
        case PlatformType.windows:
            return OperatingSystem.windows;
        case PlatformType.mac:
            return OperatingSystem.macOS;
        case PlatformType.iphone:
        case PlatformType.ipod:
        case PlatformType.ipad:
            return OperatingSystem.iOS;
        case PlatformType.android:
            return OperatingSystem.android;
        default:
            return OperatingSystem.other;
    }
};

/**
 * Determines the browser engine type (e.g. Chrome, Safari) based on the user agent, or other
 * properties we're aware of.
 *
 * NOTE: This terminology isn't accurate - the browser engine should be "WebKit" for Safari
 *   or "Blink" for Chrome, but using "safari" and "chrome" will be much clearer for most developers.
 */
const getInitialBrowserEngineType = (
    userAgent: string,
    safari: unknown,
    platformType: PlatformType,
): BrowserEngineType => {
    if (safari) return BrowserEngineType.safari;

    if (platformType === PlatformType.iphone) return BrowserEngineType.iosSafari;
    if (platformType === PlatformType.ipad) return BrowserEngineType.iosSafari;
    if (platformType === PlatformType.android && isChromeUserAgent(userAgent)) return BrowserEngineType.androidChrome;

    if (isChromeUserAgent(userAgent)) return BrowserEngineType.chrome;
    if (isFirefoxUserAgent(userAgent)) return BrowserEngineType.firefox;
    if (isEdgeUserAgent(userAgent)) return BrowserEngineType.edge;

    return BrowserEngineType.unknown;
};

/**
 * Determines the application type of the app.
 * E.g. If it's running in a "web" browser, or an "electron-mac" app, etc.
 */
export const getApplicationType = (
    capacitorPlatform: string,
    platformType: PlatformType,
    formFactor: DeviceFormFactor,
): ApplicationType => {
    if (getIsElectronMac()) return ApplicationType.electronMac;

    if (isIosPwa()) {
        if (platformType === PlatformType.ipad) return ApplicationType.ipadPwa;
        if (platformType === PlatformType.iphone) return ApplicationType.iphonePwa;
    }

    if (isNonIosDevicePwa()) {
        if (formFactor === DeviceFormFactor.phone) return ApplicationType.androidPhonePwa;
        if (formFactor === DeviceFormFactor.tablet) return ApplicationType.androidTabletPwa;
        if (platformType === PlatformType.windows) return ApplicationType.windowsPwa;
        if (platformType === PlatformType.mac) return ApplicationType.macPwa;
    }

    if (capacitorPlatform === 'ios') {
        if (platformType === PlatformType.ipad) return ApplicationType.ipadCapacitor;
        return ApplicationType.iphoneCapacitor;
    }
    if (capacitorPlatform === 'android') {
        if (formFactor == DeviceFormFactor.tablet) return ApplicationType.androidTabletCapacitor;
        return ApplicationType.androidCapacitor;
    }

    if (isInIFrame()) return ApplicationType.iframe;

    return ApplicationType.web;
};

const getAppVersionInfo = (appType: ApplicationType): { appVersion: string | null; appBuild: string | null } => {
    const milanoteCapacitor = (global as any).__milanoteCapacitor;

    switch (appType) {
        case ApplicationType.androidCapacitor:
        case ApplicationType.androidTabletCapacitor:
        case ApplicationType.iphoneCapacitor:
        case ApplicationType.ipadCapacitor:
            if (milanoteCapacitor == null) return { appVersion: null, appBuild: null };
            return {
                appVersion: milanoteCapacitor.getAppVersion() as string,
                appBuild: milanoteCapacitor.getAppBuild() as string,
            };
        default:
            return { appVersion: getClientConfig().appVersion ?? null, appBuild: null };
    }
};

/**
 * Gets a simple platform description string using the information in the platform object.
 * This is used primarily in analytics, but also occasionally API calls and element meta.
 */
export const getApplicationPlatform = (os: string, formFactor: string, appType: string): ApplicationPlatform => {
    // Apps
    if (appType === ApplicationType.iphoneSwift || appType === ApplicationType.iphoneCapacitor)
        return ApplicationPlatform.iphoneApp;
    if (appType === ApplicationType.ipadSwift || appType === ApplicationType.ipadCapacitor)
        return ApplicationPlatform.ipadApp;
    if (appType === ApplicationType.androidFlutter || appType === ApplicationType.androidCapacitor)
        return ApplicationPlatform.androidPhoneApp;
    if (appType === ApplicationType.androidTabletCapacitor) return ApplicationPlatform.androidTabletApp;

    if (appType === ApplicationType.electronMac) return ApplicationPlatform.macAppElectron;

    // PWAs
    if (appType === ApplicationType.iphonePwa) return ApplicationPlatform.iphonePwa;
    if (appType === ApplicationType.ipadPwa) return ApplicationPlatform.ipadPwa;
    if (appType === ApplicationType.androidPhonePwa) return ApplicationPlatform.androidPhonePwa;
    if (appType === ApplicationType.androidTabletPwa) return ApplicationPlatform.androidTabletPwa;
    if (appType === ApplicationType.macPwa) return ApplicationPlatform.macPwa;
    if (appType === ApplicationType.windowsPwa) return ApplicationPlatform.windowsPwa;

    // Web
    if (os === OperatingSystem.iOS) {
        if (formFactor === DeviceFormFactor.tablet) return ApplicationPlatform.ipadWeb;
        if (formFactor === DeviceFormFactor.phone) return ApplicationPlatform.iphoneWeb;
    }
    if (os === OperatingSystem.android) {
        if (formFactor === DeviceFormFactor.phone) return ApplicationPlatform.androidPhoneWeb;
        if (formFactor === DeviceFormFactor.tablet) return ApplicationPlatform.androidTabletWeb;
    }
    if (formFactor === DeviceFormFactor.desktop) return ApplicationPlatform.desktopWeb;

    logger.error('Failed to determine application platform', { os, formFactor, appType });

    return ApplicationPlatform.none;
};

/**
 * Determines the screen orientation to use.
 * If the app is running in mobile mode on a desktop form factor, we assume that it's a debugging
 * session and instead match the window orientation, rather than the screen's.
 */
export const getScreenOrientation = (
    formFactor: DeviceFormFactor,
    appMode: MilanoteApplicationMode,
): OrientationType => {
    // We're on a desktop & we're imitating a mobile app, so instead base the screen orientation
    //  on the window width and height
    if (formFactor === DeviceFormFactor.desktop && appMode === MilanoteApplicationMode.mobile) {
        return 'portrait-primary';
    }

    return global.screen?.orientation?.type || 'portrait-primary';
};

/**
 * This determines the initial state of the platform details singleton and redux store.
 */
export const getInitialPlatformDetails = (): PlatformDetails => {
    const safari = global.window?.safari;
    const userAgent = getUserAgent();
    const browserAppVersion = global.navigator?.appVersion;

    const isTouch = getIsTouchPlatform();
    // Assume a desktop screen size if it doesn't exist
    const screenWidth = global.window?.screen?.width || 2000;
    const screenHeight = global.window?.screen?.height || 1000;
    const devicePixelRatio = global.window?.devicePixelRatio || 1;

    const capacitorPlatform = getCapacitorPlatform();
    const platformType = getPlatformType({
        userAgent,
        browserAppVersion,
        screenWidth,
        screenHeight,
        isTouch,
        devicePixelRatio,
    });
    const formFactor = getFormFactor(userAgent, platformType, screenWidth);
    const appMode = getInitialAppMode(formFactor);
    const appType = getApplicationType(capacitorPlatform, platformType, formFactor);
    const browserEngine = getInitialBrowserEngineType(userAgent, safari, platformType);
    const os = getInitialOperatingSystem(platformType);
    const applicationPlatform = getApplicationPlatform(os, formFactor, appType);

    const { appVersion, appBuild } = getAppVersionInfo(appType);

    const screenOrientation = getScreenOrientation(formFactor, appMode);

    return {
        appType,
        // Start with desktop mode and switch to other modes after the user is fetched / device details are known
        appMode,
        type: platformType,
        formFactor,
        os,
        capacitorPlatform,
        // A string with a summary of platform details used for analytics and some element metadata
        applicationPlatform,
        userAgent,
        browserEngine,
        appVersion,
        appBuild,
        initialScreenWidth: screenWidth,
        initialScreenHeight: screenHeight,
        initialDevicePixelRatio: devicePixelRatio,
        screenOrientation,
        features: {
            isTouch,
            isRetina: getIsRetinaPlatform(),
            supportsMouse: getPlatformSupportsMouse(),
            supportsWebSockets: getPlatformSupportsWebSockets(),
            supportsSvg: platformSupportsSvg(),
            supportsContentEditable: platformSupportsContentEditable(),
            supportsLocalStorage: platformSupportsLocalStorage(),
            supportsIndexedDb: platformSupportsIndexedDb(),
            supportsWorker: platformSupportsWebWorkers(),
            supportsServiceWorker: platformSupportsServiceWorker(),
        },
    };
};
